import {keys} from 'ts-transformer-keys';

import {__} from '~/shared/src/utilities/messageCatalog';
import User, {View} from '~/shared/src/model/User';
import {
    ColumnConfig,
    ColumnDefinition
} from '~/shared/src/validationSchemes/modules/overview';
import {DataStructure} from '~/shared/src/utilities/dataStructure';
import dataUtilities from '~/shared/src/utilities/dataUtils';
import objectUtils from '~/shared/src/utilities/objectUtils';
import {
    getDataStructure,
    getInitialSorters,
    getItemAttributeColumnDefinitions,
    getReferenceColumnDefinitions,
    getPropertyColumnDefinitions,
    getEnumPropertyColumnDefinitions
} from '~/shared/src/utilities/modules/finder';
import {
    getColumnDefinitions,
    getItemMetaDataColumnDefinitions
} from '~/shared/src/utilities/modules/finderOverview';

import {
    addColumnToColumns,
    removeColumnFromColumns,
    getColumnConfigFromColumnDefinition,
    getColumnHeader
} from '~/frontend/src/components/base/DataTable/utilities';
import {
    ColumnSortMenuColumnsGroup,
    ColumnSortMenuSingleColumn
} from '~/frontend/src/components/base/shared/menu/ColumnSortMenu';
import {ColumnConfigMenuColumnsGroup} from '~/frontend/src/components/base/shared/menu/ColumnConfigMenu';
import FinderOverview, {
    defaultProps as finderOverviewDefaultProps,
    DefaultProps as FinderOverviewDefaultProps,
    ComponentProps as FinderOverviewComponentProps
} from '~/frontend/src/modules/panels/Finder/overview';
import {PanelProps as ViewSelectorDropdownPanelProps} from '~/frontend/src/components/base/shared/field/ViewSelectorDropdown';


export const preInitialize = ( props : FinderOverviewComponentProps & { user : User } ) : FinderOverviewComponentProps => {
    const retVal : FinderOverviewComponentProps & { user? : User } = {
        ...props
    };

    delete retVal.user;

    // set key for columns, filters and sorters if not
    if ( retVal.columns?.length ) {
        for ( let i = retVal.columns.length - 1; i >= 0; i-- ) {
            if ( !retVal.columns[ i ].key ) {
                retVal.columns[ i ].key = retVal.columns[ i ].field;
            }
        }
    }

    if ( retVal.filters?.length ) {
        for ( let i = retVal.filters.length - 1; i >= 0; i-- ) {
            if ( !retVal.filters[ i ].key ) {
                retVal.filters[ i ].key = retVal.filters[ i ].field;
            }
        }
    }

    if ( retVal.sorters?.length ) {
        for ( let i = retVal.sorters.length - 1; i >= 0; i-- ) {
            if ( !retVal.sorters[ i ].key ) {
                retVal.sorters[ i ].key = retVal.sorters[ i ].field;
            }
        }
    }

    return retVal;
}

export const initialize = ( props : FinderOverviewComponentProps & { user : User } ) : FinderOverviewComponentProps => {
    let retVal = {
        ...finderOverviewDefaultProps,
        ...props,
        panelTransformer: 'finderOverview'
    };

    retVal.dataStructure = getDataStructure( retVal.schema, retVal.itemTypeName, retVal.user );

    // no columns in state -> initialize
    if ( !retVal.columns.length ) {
        let defaults = getDefaultViewProps( retVal.schema, retVal.itemTypeName, props.user );
        if ( Object.keys( defaults ).length ) {
            defaults = buildPropsFromViewProps( defaults, retVal.dataStructure, retVal.schema, retVal.status, retVal.timestamp, retVal.itemTypeName, props.user )
        }

        if ( Object.keys( defaults ).length ) {
            retVal = {
                ...retVal,
                ...defaults
            };
        }
        else {
            retVal.sorters = getInitialSorters( retVal.dataStructure, retVal.schema, retVal.itemTypeName );
        }

        retVal.columnDefinitions = getColumnDefinitions( retVal.dataStructure, retVal.schema, retVal.status, retVal.timestamp, retVal.itemTypeName, props.user );

        // check columns against columnDefinitions
        if ( retVal.columns.length ) {
            for ( let i = retVal.columns.length - 1; i >= 0; i-- ) {
                if ( !retVal.columnDefinitions.find( colDef => colDef.field === retVal.columns[ i ].field ) ) {
                    retVal.columns.splice( i, 1 );
                }
            }
        }

        // no columns -> initial
        if ( !retVal.columns.length ) {
            retVal.columns = getInitialColumns( retVal.dataStructure, retVal.schema, retVal.status, retVal.timestamp, retVal.itemTypeName, props.user );
            //retVal.forceFit = true;
        }
    }

    // check columns not in columnDefinitions (mostly metaData columns not for current targetArea)
    else {
        retVal.columnDefinitions = getColumnDefinitions( retVal.dataStructure, retVal.schema, retVal.status, retVal.timestamp, retVal.itemTypeName, props.user );

        for ( let i = retVal.columns.length - 1; i >= 0; i-- ) {
            const columnDefinition = retVal.columnDefinitions.find( colDef => colDef.field === retVal.columns[ i ].field );

            if ( !columnDefinition ) {
                retVal.columns = ( [] as ColumnConfig[] ).concat( retVal.columns );
                retVal.columns.splice( i, 1 );
            }
        }
    }

    // remove all non allowed props
    // remove if equals default
    const allowedPropKeys = keys<FinderOverviewComponentProps>() as string[],
          propKeys        = Object.keys( retVal ) as ( keyof FinderOverviewDefaultProps )[];

    for ( const propKey of propKeys ) {
        if (
            !allowedPropKeys.includes( propKey )
            || JSON.stringify( retVal[ propKey ] ) === JSON.stringify( finderOverviewDefaultProps[ propKey ] )
        ) {
            delete retVal[ propKey ];
        }
    }

    return retVal;
}

/**
 * Builds the initial columns
 *
 * @param {DataStructure} dataStructure
 * @param {String} schema
 * @param {Status} status
 * @param {String} timestamp
 * @param {String} itemTypeName
 * @param {User} user
 *
 * @return {ColumnConfig[]}
 */
export const getInitialColumns = ( dataStructure : DataStructure, schema : Schema, status : Status, timestamp : string, itemTypeName : string, user : User ) : ColumnConfig[] => {
    const defaultProps     = getDefaultViewProps( schema, itemTypeName, user ),
          defaultShowCount = 3;

    let retVal : ColumnConfig[] = [];

    // get columns from default view if
    if ( defaultProps.columns?.length ) {
        retVal = defaultProps.columns;
    }
    else {
        const columnDefinitions = getColumnDefinitions( dataStructure, schema, status, timestamp, itemTypeName, user );

        for ( let i = 0; i < columnDefinitions.length, i < defaultShowCount; i++ ) {
            retVal.push( getColumnConfigFromColumnDefinition( columnDefinitions[ i ] ) );
        }
    }

    return retVal;
}

/**
 * Gets the module for viewSelector.
 *
 * @param {Schema} schema
 * @param {string} itemTypeName
 */
export const getModuleForViewSelector = ( schema : Schema, itemTypeName : string ) : string[] => {
    return [
        FinderOverview.viewSelectorModule,
        schema,
        itemTypeName
    ];
}

/**
 * Gets the views from user.
 *
 * @param {Schema} schema
 * @param {string} itemTypeName
 * @param {User} user
 */
export const getViews = ( schema : Schema, itemTypeName : string, user : User ) : View[] => {
    const retVal = user.getViewsForModule( getModuleForViewSelector( schema, itemTypeName ) ) ?? [];

    // set key for columns, filters and sorters if not
    for ( let viewIndex = 0; viewIndex < retVal.length; viewIndex++ ) {
        if ( retVal[ viewIndex ].panelProps.columns?.length ) {
            for ( let i = retVal[ viewIndex ].panelProps.columns.length - 1; i >= 0; i-- ) {
                if ( !retVal[ viewIndex ].panelProps.columns[ i ].key ) {
                    retVal[ viewIndex ].panelProps.columns[ i ].key = retVal[ viewIndex ].panelProps.columns[ i ].field;
                }
            }
        }

        if ( retVal[ viewIndex ].panelProps.filters?.length ) {
            for ( let i = retVal[ viewIndex ].panelProps.filters.length - 1; i >= 0; i-- ) {
                if ( !retVal[ viewIndex ].panelProps.filters[ i ].key ) {
                    retVal[ viewIndex ].panelProps.filters[ i ].key = retVal[ viewIndex ].panelProps.filters[ i ].field;
                }
            }
        }

        if ( retVal[ viewIndex ].panelProps.sorters?.length ) {
            for ( let i = retVal[ viewIndex ].panelProps.sorters.length - 1; i >= 0; i-- ) {
                if ( !retVal[ viewIndex ].panelProps.sorters[ i ].key ) {
                    retVal[ viewIndex ].panelProps.sorters[ i ].key = retVal[ viewIndex ].panelProps.sorters[ i ].field;
                }
            }
        }
    }

    // reorder by label
    retVal.sort( ( val1, val2 ) => {
        if ( val1.label > val2.label ) {
            return 1;
        }
        else if ( val1.label < val2.label ) {
            return -1;
        }
        else {
            return 0;
        }
    } );

    return retVal;
}

/**
 * Gets the default view for given parameters from the given user if there are some.
 *
 * @param {Schema} schema
 * @param {string} itemTypeName
 * @param {User} user
 *
 * @return {Partial<OverviewComponentProps>}
 */
export const getDefaultViewProps = ( schema : Schema, itemTypeName : string, user : User ) : Partial<FinderOverviewComponentProps> => {
    const views = getViews( schema, itemTypeName, user );

    let retVal : Partial<FinderOverviewComponentProps> = {};

    for ( const view of views ) {
        if ( view.panelProps.default ) {
            retVal = view.panelProps;
            break;
        }
    }

    return retVal;
}

/**
 * Build the full set of props to set for given viewProps.
 *
 * @param props
 * @param {DataStructure} dataStructure
 * @param {String} schema
 * @param {Status} status
 * @param {String} timestamp
 * @param {String} itemTypeName
 * @param {User} user
 * @param {( keyof FinderOverviewComponentProps )[]} excludePropKeys
 *
 * @return {Partial<FinderOverviewDefaultProps>}
 */
export const buildPropsFromViewProps = ( props : Partial<FinderOverviewDefaultProps>, dataStructure : DataStructure, schema : Schema, status : Status, timestamp : string, itemTypeName : string, user : User, excludePropKeys : ( keyof FinderOverviewComponentProps )[] = [] ) : Partial<FinderOverviewDefaultProps> => {
    props = {
        ...finderOverviewDefaultProps,
        ...props
    };

    const retVal                 = {} as Partial<FinderOverviewDefaultProps>,
          propKeys               = Object.keys( props ) as ( keyof FinderOverviewDefaultProps )[],
          allowedPropKeys        = keys<FinderOverviewDefaultProps>() as ( keyof FinderOverviewComponentProps )[],
          defaultExcludePropKeys = [
              'schema',             // defined by component
              'itemTypeName',       // defined by component
              'dataStructure',      // computed
              'columnDefinitions',  // computed
              'editable',           // computed
              'className',          // defined by component
              'doSearch',
              'scrollLeft',
              'scrollTop'
          ] as ( keyof FinderOverviewComponentProps )[];

    // allowed prop (only FinderOverviewDefaultProps are allowed)
    // no default exclude prop
    // no given exclude prop
    for ( const propKey of propKeys ) {
        if (
            allowedPropKeys.includes( propKey )
            && !defaultExcludePropKeys.includes( propKey )
            && !excludePropKeys.includes( propKey )
        ) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            retVal[ propKey ] = props[ propKey ];
        }
    }

    // add columnDefinition
    retVal.columnDefinitions = getColumnDefinitions( dataStructure, schema, status, timestamp, itemTypeName, user );

    retVal.doSearch = retVal.search;

    return retVal;
}

/**
 * Extracts the minimal viewProps from given props.
 *
 * @param {FinderOverviewComponentProps} props
 * @param {( keyof FinderOverviewComponentProps )[]} excludePropKeys
 *
 * @return {Partial<FinderOverviewComponentProps>}
 */
export const extractViewPropsFromProps = ( props : FinderOverviewComponentProps, excludePropKeys : ( keyof FinderOverviewComponentProps )[] = [] ) : Partial<FinderOverviewComponentProps> => {
    const retVal                 = {} as Partial<FinderOverviewComponentProps>,
          propKeys               = Object.keys( props ) as ( keyof FinderOverviewComponentProps )[],
          allowedPropKeys        = keys<FinderOverviewDefaultProps>() as string[],
          defaultExcludePropKeys = [
              'schema',             // defined by component
              'itemTypeName',       // defined by component
              'dataStructure',      // computed
              'columnDefinitions',  // computed
              'actionColumns',      // computed
              'editable',           // computed
              'forceFit',           // will be always false
              'autoLayout',         // will be always false
              'className',          // defined by component
              'doSearch',
              'scrollLeft',
              'scrollTop'
          ] as ( keyof FinderOverviewComponentProps )[];

    // allowed prop (only FinderOverviewDefaultProps are allowed)
    // no default exclude prop
    // no given exclude prop
    // not equals default
    for ( const propKey of propKeys ) {
        if (
            allowedPropKeys.includes( propKey )
            && !defaultExcludePropKeys.includes( propKey )
            && !excludePropKeys.includes( propKey )
            && JSON.stringify( props[ propKey ] ) !== JSON.stringify( finderOverviewDefaultProps[ propKey as keyof FinderOverviewDefaultProps ] )
        ) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            retVal[ propKey ] = props[ propKey ];
        }
    }

    return retVal;
}

/**
 * Builds the grouped columns with visibility.
 *
 * @param {ColumnDefinition[]} columnDefinitions
 * @param {ColumnConfig[]} columns
 * @param {DataStructure} dataStructure
 * @param {Schema} schema
 * @param {Status} status
 * @param {string} timestamp
 * @param {number} itemTypeName
 * @param {boolean} rawMode
 * @param {boolean} showAdditionalRawNames
 * @param {User} user
 *
 * @return {ColumnSortMenuColumnsGroup[]}
 */
export const getAllColumns = ( columnDefinitions : ColumnDefinition[], columns : ColumnConfig[], dataStructure : DataStructure, schema : Schema, status : Status, timestamp : string, itemTypeName : string, rawMode : boolean, showAdditionalRawNames : boolean, user : User ) : ColumnSortMenuColumnsGroup[] => {
    const retVal : ColumnSortMenuColumnsGroup[] = [],
          groupColumnExtractor                  = ( groupColumnFields : string[] ) => {
              const innerRetVal : ColumnSortMenuSingleColumn[] = [];

              for ( const columnDefinition of columnDefinitions ) {
                  if ( groupColumnFields.includes( columnDefinition.field ) ) {
                      const column = columns.find( colConfig => colConfig.field === columnDefinition.field );

                      innerRetVal.push( {
                          key      : columnDefinition.key ?? columnDefinition.field,
                          field    : columnDefinition.field,
                          header   : getColumnHeader( columnDefinition, dataStructure, schema, rawMode, showAdditionalRawNames ),
                          hidden   : !column,
                          hidable  : columnDefinition.hidable ?? true,
                          valueType: dataUtilities.getDataValueType( columnDefinition.dataDescriptor, schema, dataStructure ) ?? 'STRING'
                      } );
                  }
              }

              return innerRetVal;
          };

    // parentReference and item attribute columns
    retVal.push( {
        key    : 'showAttributes',
        label  : __( 'YN.base.finder.attributes.label' ),
        columns: groupColumnExtractor( getItemAttributeColumnDefinitions( dataStructure ).map( colDef => colDef.field ) )
    } );

    // property columns
    retVal.push( {
        key    : 'showProperties',
        label  : __( 'YN.base.finder.properties.label' ),
        columns: groupColumnExtractor( getPropertyColumnDefinitions( dataStructure, schema, itemTypeName, user ).map( colDef => colDef.field ) )
    } );

    // enumerated property columns
    retVal.push( {
        key    : 'showEnumProperties',
        label  : __( 'YN.base.finder.enumProperties.label' ),
        columns: groupColumnExtractor( getEnumPropertyColumnDefinitions( dataStructure, schema, itemTypeName, user ).map( colDef => colDef.field ) )
    } );

    // reference columns
    retVal.push( {
        key    : 'showReferences',
        label  : __( 'YN.base.finder.references.label' ),
        columns: groupColumnExtractor( getReferenceColumnDefinitions( dataStructure, schema, itemTypeName, user ).map( colDef => colDef.field ) )
    } );

    // metaData columns
    retVal.push( {
        key    : 'showMetaData',
        label  : __( 'YN.base.finder.metaData.label' ),
        columns: groupColumnExtractor( getItemMetaDataColumnDefinitions( dataStructure, status, timestamp ).map( colDef => colDef.field ) )
    } );

    return retVal;
}

/**
 * Applies the visibility given by allColumnsGrouped to columns by adding or removing appropriate columns.
 *
 * @param {ColumnConfigMenuColumnsGroup[]} allColumnsGrouped
 * @param {ColumnDefinition[]} columnDefinitions
 * @param {ColumnConfig[]} [columns]
 *
 * @return {ColumnConfig[]}
 */
export const applyAllColumnsToColumns = ( allColumnsGrouped : ColumnConfigMenuColumnsGroup[], columnDefinitions : ColumnDefinition[], columns : ColumnConfig[] ) : ColumnConfig[] => {
    let retVal : ColumnConfig[] = ( [] as ColumnConfig[] ).concat( columns );

    // iterate column groups
    for ( let i = allColumnsGrouped.length - 1; i >= 0; i-- ) {
        const groupColumns = allColumnsGrouped[ i ].columns;

        // iterate columns
        for ( let k = 0; k < groupColumns.length; k++ ) {
            const column           = groupColumns[ k ],
                  columnDefinition = columnDefinitions.find( colDef => colDef.field === column.field ),
                  columnConfig     = columns.find( colConfig => colConfig.field === column.field );

            if ( columnDefinition ) {

                // add column
                if ( !column.hidden && !columnConfig ) {
                    retVal = addColumnToColumns( columnDefinition, retVal, columnDefinitions );
                }

                // remove column
                else if ( column.hidden && columnConfig ) {
                    retVal = removeColumnFromColumns( columnDefinition, retVal );
                }
            }
        }
    }

    return retVal;
}

/**
 * Compares the given panelProps with the given viewPanelProps.
 *
 * @param {ViewSelectorDropdownPanelProps} panelProps
 * @param {ViewSelectorDropdownPanelProps} viewPanelProps
 *
 * @return {boolean}
 *
 * @private
 */
export const viewSelectorPanelPropsCompare = ( panelProps : ViewSelectorDropdownPanelProps, viewPanelProps : ViewSelectorDropdownPanelProps ) : boolean => {
    const forceFit   = viewPanelProps.forceFit || false,
          autoLayout = viewPanelProps.autoLayout || false;

    panelProps = extractViewPropsFromProps( panelProps as FinderOverviewComponentProps );
    viewPanelProps = extractViewPropsFromProps( viewPanelProps as FinderOverviewComponentProps );

    delete panelProps.search;
    delete viewPanelProps.search;

    if ( !panelProps.rowStyling?.length ) {
        delete panelProps.rowStyling;
    }
    if ( !viewPanelProps.rowStyling?.length ) {
        delete viewPanelProps.rowStyling;
    }

    if ( panelProps.columns ) {
        panelProps.columns = [].concat( panelProps.columns );

        // remove parentReference columns
        // remove action columns
        // remove column width if forceFit or autoLayout
        for ( let i = panelProps.columns.length - 1; i >= 0; i-- ) {
            if ( panelProps.columns[ i ].parentReference || panelProps.columns[ i ].action ) {
                panelProps.columns.splice( i, 1 );
            }

            if ( forceFit || autoLayout ) {
                panelProps.columns[ i ] = Object.assign( {}, panelProps.columns[ i ] ) as ColumnConfig;
                delete panelProps.columns[ i ].width;
            }
        }
    }

    if ( viewPanelProps.columns ) {
        viewPanelProps.columns = [].concat( viewPanelProps.columns );

        // remove parentReference columns
        // remove action columns
        // remove column width if forceFit or autoLayout
        for ( let i = viewPanelProps.columns.length - 1; i >= 0; i-- ) {
            if ( viewPanelProps.columns[ i ].parentReference || viewPanelProps.columns[ i ].action ) {
                viewPanelProps.columns.splice( i, 1 );
            }

            if ( forceFit || autoLayout ) {
                viewPanelProps.columns[ i ] = Object.assign( {}, viewPanelProps.columns[ i ] ) as ColumnConfig;
                delete viewPanelProps.columns[ i ].width;
            }
        }
    }

    return !objectUtils.getDiffKeys( panelProps, viewPanelProps ).length;
}
